import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../api';
import AuthLayout, { Field, Alert, inputClass, labelClass, primaryBtnClass } from '../components/AuthLayout';
import PasswordInput from '../components/PasswordInput';
import OtpInput from '../components/OtpInput';

const RESEND_COOLDOWN = 30;

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Deployments with no SMTP (EXPOSE_RESET_OTP) hand the code back in the
  // response instead of emailing it — we prefill it and say so.
  const [inlineCode, setInlineCode] = useState(false);
  // Inlined codes by the (normalised) email they were issued for. A response
  // with no code can then only ever restore THIS email's code, never one left
  // over from a different address entered earlier.
  const inlineCodes = useRef({});

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await forgotPassword(email);
      const key = email.trim().toLowerCase();
      const code = res?.data?.otp || '';
      if (code) inlineCodes.current[key] = code;
      // No code in the response: either a real email went out, or (inline mode)
      // the server's resend cooldown refused a NEW code — in which case the one
      // issued earlier for this same email is still the valid one.
      const known = code || inlineCodes.current[key] || '';
      setOtp(known);
      setInlineCode(Boolean(known));
      setCooldown(RESEND_COOLDOWN);
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');
    setLoading(true);
    try {
      await resetPassword(email, otp, password);
      navigate('/login', { replace: true, state: { reset: true } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'reset') {
    return (
      <AuthLayout
        title="Set a new password"
        onBack={() => setStep('email')}
        subtitle={
          inlineCode ? (
            <>
              Email delivery is disabled on this deployment, so your code is filled in below
            </>
          ) : (
            <>
              Enter the code sent to <span className="text-fg font-medium">{email}</span>
            </>
          )
        }
        footer={
          <Link to="/login" className="text-orange hover:underline font-medium">
            Back to sign in
          </Link>
        }
      >
        <form onSubmit={submitReset}>
          <Alert>{error}</Alert>
          <div className="mb-5">
            <label className={labelClass}>Verification code</label>
            <OtpInput value={otp} onChange={setOtp} />
            <p className="text-right text-xs text-fg-muted mt-2">
              {cooldown > 0 ? (
                <span>Resend in {cooldown}s</span>
              ) : (
                <button type="button" onClick={() => requestCode()} className="text-orange hover:underline">
                  Resend code
                </button>
              )}
            </p>
          </div>
          <Field label="New password" hint="At least 8 characters">
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a new password"
              autoComplete="new-password"
            />
          </Field>
          <div className="mb-5">
            <label className={labelClass}>Confirm password</label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            {confirm && confirm !== password && <p className="mt-1.5 text-xs text-red-400">Passwords do not match</p>}
          </div>
          <button type="submit" disabled={loading || otp.length < 6} className={primaryBtnClass}>
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a code to reset it"
      footer={
        <Link to="/login" className="text-orange hover:underline font-medium">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={requestCode}>
        <Alert>{error}</Alert>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
        </Field>
        <button type="submit" disabled={loading} className={primaryBtnClass}>
          {loading ? 'Sending…' : 'Send reset code'}
        </button>
      </form>
    </AuthLayout>
  );
}

export default ForgotPassword;
